import { useId, useState } from 'react';
import { Link } from 'react-router';

import {
  ACTION_LABELS,
  CATEGORIES,
  CATEGORY_LABELS,
  DESTINATION_LABELS,
  DESTINATIONS,
  type EmailCategory,
  type TriageDestination,
} from '@/shared/triage/vocabulary';
import { isManager, useSessionUser } from '@/shared/session/currentUser';
import type { NoteLearning } from '@/shared/triage/guidelines';
import { DestinationDot } from '@/shared/triage/DestinationName';
import { Button } from '@/shared/ui/Button';
import { actionFailureText } from '@/shared/emails/mailFailure';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input, Select } from '@/shared/ui/Input';
import { CheckIcon } from '@/shared/ui/icons';
import { TechnicalDetails } from '@/shared/ui/TechnicalDetails';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';

import { CategoryBadge } from './CategoryBadge';
import {
  actionReasons,
  actionState,
  actionWords,
  confirmDetail,
  confirmForwardNote,
  confirmLabel,
  correctionFolderText,
  correctionLabel,
  hasTechnicalTail,
  lacksAddress,
  type ActionState,
  type ActionWords,
  type ForwardNote,
} from './confirmState';
import { NoteLearningNote } from './NoteLearningNote';
import {
  agreesText,
  bulkFooterText,
  doubtReasons,
  examplesText,
  guidelineNote,
  memoryNote,
  precedentNote,
  rationaleText,
  reviewTexts,
  UNSURE_TEXT,
} from './triageWhy';
import { decisionCategory, type TriageDecision, type TriagePlan } from './types';
import { useCorrectTriage } from './useEmails';

const WAITING = 'espera a que alguien confirme';

/**
 * Under «¿Por qué?» of «Corregir…» (contract v10.35): the note is what the platform learns
 * from, and the two examples are the two reaches a note can have.
 */
export const NOTE_HINT =
  'Si escribes por qué, la plataforma aprende antes. Por ejemplo: «Todos los pedidos de este cliente van a María» o «Este lo miro yo esta vez».';

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'long', timeStyle: 'short' });
const timeFormat = new Intl.DateTimeFormat('es-ES', { timeStyle: 'short' });

/**
 * What the triage decided about one email, and the two buttons that judge it.
 *
 * This is the whole learning loop of the project (contexto §3.7): the pair
 * proposal/correction is what turns a demo into an accuracy figure, and the figure is
 * what closes H3. Both buttons write to the same endpoint, because a confirmation is
 * data exactly as much as a correction is.
 *
 * Since contract v10 confirming can also **act**: the button says what it is going to
 * do before the click, reads `triage_plan` for it, and the panel follows the action
 * from «Reenviando…» (or «Moviéndolo…», `actionWords`) to «Reenviado a … a las 10:32» or
 * to the error and Reintentar.
 *
 * What the panel says, in order: what it is (the category), where it goes, why (the
 * rationale, one line), whether anybody reviewed it, and the buttons. The model's name,
 * the percentage, the prompt version and the id of the redirect are not shown at all:
 * the customer has no use for them, and whoever debugs has the database and Sentry.
 *
 * Since contract v10.30 the «why» is richer, in the same quiet lines and without new
 * boxes (`triageWhy.ts`): when the sender memory changed the proposal, the day of the
 * correction and what it would have been without it; when the model saw answered emails,
 * how many; and when the system is not sure, whether the destination came from the doubt
 * rule or the proposal should be looked at, and why. A doubt never changes the destination.
 *
 * Since contract v10.35 (ADR 0030 of the back) what the system learned is named the same
 * way: the criterion distilled from somebody's note («Criterio de Víctor (25/09): …») or
 * the answered email of the same sender it followed, with a link to it. And after a
 * correction with a note, what that note taught (`NoteLearningNote`), with «Deshacer».
 */
export function TriagePanel({
  emailId,
  decision,
  plan,
  movedTo = null,
  noteLearning = null,
}: {
  emailId: string;
  decision: TriageDecision;
  plan?: TriagePlan | null | undefined;
  /** Where the platform moved the original in the mailbox (contract v10.25), if it did. */
  movedTo?: string | null;
  /** What the note on this decision taught (contract v10.35), if it has a note. */
  noteLearning?: NoteLearning | null | undefined;
}) {
  const proposed = decision.proposed_destination;
  const final = decision.final_destination ?? null;
  const goesTo = final ?? proposed;
  // What the email is now: the person's category if they corrected it (contract v10.24).
  const liveCategory = decisionCategory(decision);
  const correctedCategory =
    decision.corrected_category && decision.corrected_category !== decision.category
      ? decision.corrected_category
      : null;
  // «Corregir…»: what it is and where it goes, starting from what it is now. Reset to
  // that on success, so a choice abandoned half way cannot be sent by a stray second
  // click after «Correcto» already recorded the opposite.
  const [category, setCategory] = useState<EmailCategory>(liveCategory);
  const [chosen, setChosen] = useState<TriageDestination | ''>('');
  const [note, setNote] = useState('');
  const correct = useCorrectTriage(emailId, {
    onSuccess: () => {
      setChosen('');
      setNote('');
    },
  });
  const selectId = useId();
  const changed = category !== liveCategory || (Boolean(chosen) && chosen !== (final ?? proposed));

  function chooseCategory(next: EmailCategory): void {
    setCategory(next);
    // The routing table of this mailbox, from the server (`destination_by_category`):
    // «Correo basura» stays, «Pedido sin albarán» goes to Paulo. The person can still
    // change it; what they cannot do is forget to.
    const routed = plan?.destination_by_category?.[next];
    if (routed) setChosen(routed);
  }

  function saveCorrection(): void {
    const target = chosen || final || proposed;
    correct.mutate({
      final_destination: target,
      // Only when it differs from the proposal: the proposal stays in `category` and the
      // person's word goes next to it (the back never overwrites what it proposed).
      category: category !== decision.category ? category : null,
      note: note.trim() || null,
    });
  }
  // Why it is what it is (contract v10.30). The two doubts that ask for a review are
  // dropped once somebody answered: «Revísalo» under «Marcado como correcto» would ask for
  // what already happened. The doubt rule's line stays, it explains where the email went.
  const doubts = doubtReasons(decision);
  const unsure = doubts.includes('confianza_baja');
  const toReview = final ? [] : reviewTexts(doubts);
  const memory = memoryNote(decision);
  const guideline = guidelineNote(decision);
  const precedent = precedentNote(decision);
  const rationale = rationaleText(decision, memory ?? guideline ?? precedent);
  const agrees = agreesText(decision);
  const bulk = bulkFooterText(decision);
  const examples = examplesText(decision);
  const action = actionState(decision);
  const waiting = plan?.forward_mode === 'confirm' && !decision.forwarded_to;
  const busy = correct.isPending || action.kind === 'sending';
  const forwardNote = confirmForwardNote(plan, proposed);
  // «Se hizo: Nada · espera a que alguien confirme» already says it; a box repeating it
  // twice more is noise between the proposal and the button.
  const saysWaiting = decision.action_taken === 'none' && waiting;
  const reasons = decision.action_error ? actionReasons(decision.action_error) : [];
  const whyNothing =
    saysWaiting && reasons.every((reason) => reason.endsWith(WAITING)) ? [] : reasons;
  const manager = isManager(useSessionUser());

  return (
    <div className="flex flex-col gap-3">
      {/* The decision: what it is, where it goes, why. */}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <CategoryBadge category={liveCategory} size="lg" />
          {correctedCategory ? (
            <span className="text-ink-3 text-[12.5px]">
              Propuesto: {CATEGORY_LABELS[decision.category]}
            </span>
          ) : null}
        </div>
        {/* Where it goes now: the person's answer when there is one (contract v10.24), with
            the proposal beside it, like the category above. */}
        <p className="text-ink text-[15px]">
          {goesTo === 'se_queda' ? (
            <>
              <strong>{DESTINATION_LABELS[goesTo]}</strong> en el buzón
            </>
          ) : (
            <>
              Va a{' '}
              <strong className="inline-flex items-center gap-1.5">
                <DestinationDot destination={goesTo} />
                {DESTINATION_LABELS[goesTo]}
              </strong>
            </>
          )}
          {goesTo !== proposed ? (
            <span className="text-ink-3 ml-2 text-[12.5px]">
              Propuesto: {DESTINATION_LABELS[proposed]}
            </span>
          ) : null}
        </p>
        {unsure ? <p className="text-warn text-[13.5px]">{UNSURE_TEXT}</p> : null}
        {toReview.length > 0 ? <ReviewNote texts={toReview} /> : null}
        {/* What the sender memory did, when it decided: the day of the correction, and in
            small print what the proposal would have been without it. It takes the place of
            the back's own sentence in the rationale, which said the same. */}
        {memory ? (
          <div className="flex flex-col gap-0.5">
            <p className="text-ink-2 text-[13.5px] leading-snug">{memory.sentence}</p>
            {memory.without ? <p className="text-ink-3 text-[12.5px]">{memory.without}</p> : null}
          </div>
        ) : null}
        {/* What a note taught that changed the proposal (contract v10.35): the criterion,
            who wrote it and when, and what it would have been without it. */}
        {guideline ? (
          <div className="flex flex-col gap-0.5">
            <p className="text-ink-2 text-[13.5px] leading-snug">{guideline.sentence}</p>
            {guideline.without ? (
              <p className="text-ink-3 text-[12.5px]">{guideline.without}</p>
            ) : null}
          </div>
        ) : null}
        {/* An answered email of the same sender the model judged the same kind: its day,
            and the email itself one click away. */}
        {precedent ? (
          <div className="flex flex-col gap-0.5">
            <p className="text-ink-2 text-[13.5px] leading-snug">
              {precedent.sentence}
              {precedent.emailId ? (
                <>
                  {' · '}
                  <Link to={`/correos/${precedent.emailId}`} className="link">
                    Ver ese correo
                  </Link>
                </>
              ) : null}
            </p>
            {precedent.without ? (
              <p className="text-ink-3 text-[12.5px]">{precedent.without}</p>
            ) : null}
          </div>
        ) : null}
        {rationale ? (
          <p className="text-ink-3 text-[13.5px] leading-snug italic">{rationale}</p>
        ) : null}
        {agrees ? <p className="text-ink-3 text-[12.5px]">{agrees}</p> : null}
        {bulk ? <p className="text-ink-3 text-[13.5px] leading-snug">{bulk}</p> : null}
        {examples ? <p className="text-ink-3 text-[12.5px]">{examples}</p> : null}
      </div>

      {/* What was really done, while nothing is on its way: a proposed destination is
          not a delivered email, and the panel has to say so. Once a confirmation is in
          flight or landed, `ActionStatus` below tells that story. */}
      {action.kind === 'idle' ? (
        <p className="text-ink-3 text-[13px]">
          Se hizo: <span className="text-ink">{ACTION_LABELS[decision.action_taken]}</span>
          {decision.forwarded_to ? (
            <>
              {' a '}
              <span className="text-ink">{decision.forwarded_to}</span>
            </>
          ) : null}
          {decision.labelled_as ? (
            <>
              {' · carpeta '}
              <span className="text-ink">{decision.labelled_as}</span>
            </>
          ) : null}
          {decision.acted_at ? (
            <>
              {' · '}
              <time dateTime={decision.acted_at}>{formatDate(dateFormat, decision.acted_at)}</time>
            </>
          ) : null}
          {saysWaiting ? ` · ${WAITING}` : null}
        </p>
      ) : null}

      {/* Why nothing was done, when nothing was. It is the only operational question F4
          raises, and the back answers it with the name of the condition that refused. */}
      {whyNothing.length > 0 && action.kind !== 'failed' ? (
        <WhyNothing
          reasons={whyNothing}
          technical={
            decision.action_error && hasTechnicalTail(decision.action_error)
              ? decision.action_error
              : null
          }
        />
      ) : null}

      {final ? (
        <p
          className={cn(
            'flex items-start gap-2 rounded-md px-3 py-2 text-[13.5px]',
            final === proposed && !correctedCategory
              ? 'bg-ok-soft text-ok'
              : 'bg-warn-soft text-warn',
          )}
        >
          <CheckIcon size={16} className="mt-0.5 shrink-0" />
          <span>
            {final === proposed && !correctedCategory
              ? 'Marcado como correcto'
              : `Corregido a ${[
                  correctedCategory ? CATEGORY_LABELS[correctedCategory] : null,
                  DESTINATION_LABELS[final],
                ]
                  .filter(Boolean)
                  .join(' · ')}`}
            {decision.corrected_at ? (
              <span className="opacity-70"> · {formatDate(dateFormat, decision.corrected_at)}</span>
            ) : null}
          </span>
        </p>
      ) : (
        <p className="text-ink-3 text-[13px]">Nadie lo ha revisado todavía.</p>
      )}

      {/* What the note taught (contract v10.35). Always mounted and displayed, only the
          content changes, so «Aprendiendo…» turning into «Aprendido: …» is announced (a
          region with `display: none` is not); empty, the negative margin takes back the
          gap of the column. */}
      <div aria-live="polite" className="empty:-mt-3">
        {noteLearning ? <NoteLearningNote emailId={emailId} learning={noteLearning} /> : null}
      </div>

      {/* The confirmed action, followed from the click to the outcome (contract v10). */}
      <ActionStatus
        state={action}
        words={actionWords(plan)}
        moves={Boolean(plan?.will_move)}
        moved={Boolean(movedTo)}
        retry={
          final
            ? () => correct.mutate({ final_destination: final })
            : () => correct.mutate({ final_destination: proposed })
        }
        busy={correct.isPending}
      />

      <div className="border-line-2 flex flex-col gap-2 border-t pt-3">
        <p className="text-ink text-[14px] font-medium">¿Ha acertado?</p>
        {/* Once answered, the red button is «Siguiente por revisar» below: this one stays,
            quieter, for changing one's mind. */}
        <Button
          variant={final ? 'secondary' : 'primary'}
          icon={<CheckIcon size={18} />}
          onClick={() => correct.mutate({ final_destination: proposed })}
          disabled={busy}
          wrap
        >
          {confirmLabel(plan, proposed)}
        </Button>
        {confirmDetail(plan) ? (
          <p className="text-ink-3 text-[12.5px] leading-snug [overflow-wrap:anywhere]">
            {confirmDetail(plan)}
          </p>
        ) : null}
        {forwardNote ? <ForwardNoteLine note={forwardNote} manager={manager} /> : null}

        {/* The correction, folded: one click opens the list, so the screen keeps one
            red button and the common case (it was right) stays one click. */}
        <details className="group">
          <summary className="text-ink-2 hover:bg-paper-2 hover:text-ink focus-visible:outline-accent inline-flex h-9 cursor-pointer list-none items-center gap-1.5 rounded-md px-3 text-[14px] font-medium select-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 [&::-webkit-details-marker]:hidden">
            Corregir…
            <span aria-hidden="true" className="text-ink-4 text-[10px] group-open:rotate-180">
              ▾
            </span>
          </summary>
          <div className="mt-2 flex flex-col gap-2">
            {/* What it is first (contract v10.24): «es / no es oferta de proveedor» and
                the rest. Changing it proposes where it goes, from the mailbox's table. */}
            <Select
              id={`${selectId}-que`}
              label="Qué es"
              value={category}
              onChange={(event) => chooseCategory(event.target.value as EmailCategory)}
            >
              {CATEGORIES.map((each) => (
                <option key={each} value={each}>
                  {CATEGORY_LABELS[each]}
                </option>
              ))}
            </Select>
            <Select
              id={selectId}
              label="A quién va"
              value={chosen || final || proposed}
              onChange={(event) => setChosen(event.target.value as TriageDestination)}
            >
              {DESTINATIONS.map((each) => (
                <option key={each} value={each}>
                  {DESTINATION_LABELS[each]}
                  {lacksAddress(plan, each) ? ' (sin dirección)' : ''}
                </option>
              ))}
            </Select>
            <Input
              id={`${selectId}-nota`}
              label="¿Por qué? (opcional)"
              value={note}
              maxLength={500}
              hint={NOTE_HINT}
              onChange={(event) => setNote(event.target.value)}
            />
            {category !== liveCategory ? (
              <p className="text-ink-3 text-xs">
                {correctionFolderText(plan, category)}
                {category === 'oferta_proveedor_respuesta'
                  ? ' Se leerá la oferta y aparecerá en Presupuestos.'
                  : liveCategory === 'oferta_proveedor_respuesta'
                    ? ' Si dejó un borrador en Presupuestos sin número, se anula.'
                    : ''}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                wrap
                disabled={!changed || busy}
                aria-describedby={changed ? undefined : `${selectId}-hint`}
                onClick={saveCorrection}
              >
                {correctionLabel(plan, chosen)}
              </Button>
              {/* A disabled button that does not say why is a button the user clicks twice
                  and then gives up on. */}
              <p id={`${selectId}-hint`} hidden={changed} className="text-ink-3 text-xs">
                Cambia qué es o a quién va.
              </p>
            </div>
            {chosen && lacksAddress(plan, chosen) ? (
              <p className="text-warn text-xs">
                {DESTINATION_LABELS[chosen]} no tiene dirección: se guarda la corrección y no se
                reenvía.
              </p>
            ) : null}
          </div>
        </details>
      </div>

      {correct.isError ? (
        <ErrorMessage error={correct.error}>No se pudo guardar</ErrorMessage>
      ) : null}
      {/* Always mounted, only the text changes. A live region inserted whole is missed
          by NVDA and JAWS often enough that it cannot be relied on; one that already
          existed in the DOM is announced. The lasting confirmation is the block above,
          which says what was recorded instead of just that something was. */}
      <p role="status" aria-live="polite" className="text-ok min-h-0 text-sm">
        {correct.isSuccess ? 'Guardado.' : ''}
      </p>
    </div>
  );
}

/**
 * «Revísalo: …» in the warning colour of the panel: the model doubted itself and the
 * proposal should be looked at (`vecinos`, `segunda_opinion`). One doubt is one line; two
 * are a list under «Revísalo:», each still a whole sentence, the idiom of `WhyNothing`.
 */
function ReviewNote({ texts }: { texts: string[] }) {
  if (texts.length === 1) {
    return <p className="text-warn text-[13.5px]">Revísalo: {texts[0]}.</p>;
  }
  return (
    <div className="text-warn text-[13.5px]">
      <p>Revísalo:</p>
      <ul className="list-disc pl-4">
        {texts.map((text) => (
          <li key={text}>{text}.</li>
        ))}
      </ul>
    </div>
  );
}

/** Why nothing was done at classification, one line per half (`actionReasons`). */
function WhyNothing({ reasons, technical }: { reasons: string[]; technical: string | null }) {
  return (
    <div className="bg-paper-2 text-ink-2 rounded-md px-3 py-2 text-[13px]">
      <span className="text-ink font-medium">Por qué: </span>
      {reasons.length === 1 ? (
        reasons[0]
      ) : (
        <ul className="mt-1 list-disc pl-4">
          {reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
      {technical ? <TechnicalDetails text={technical} /> : null}
    </div>
  );
}

/**
 * Why Correcto will not redirect, under the button. A missing address is said louder
 * than the rest and with the way out, because it is the one a manager fixes in a
 * minute and the one that otherwise goes unnoticed: «Va a Paulo» above, a button that
 * only copies to a folder, and Paulo never gets the order.
 */
function ForwardNoteLine({ note, manager }: { note: ForwardNote; manager: boolean }) {
  if (!note.missingAddress) {
    return <p className="text-ink-3 text-xs">No se reenvía por pulsar: {note.reason}.</p>;
  }
  return (
    <p role="note" className="bg-warn-soft text-warn rounded-md px-3 py-2 text-[13px]">
      <span className="font-medium">No se reenvía por pulsar: {note.reason}.</span>{' '}
      {note.staysInInbox
        ? 'El correo se queda en tu bandeja de entrada hasta que la tenga. '
        : null}
      {manager ? (
        <Link to="/administracion/destinos" className="link">
          Ponerla en Destinos
        </Link>
      ) : (
        'Quien administra la pone en Administración → Destinos.'
      )}
    </p>
  );
}

function ActionStatus({
  state,
  words,
  moves,
  moved,
  retry,
  busy,
}: {
  state: ActionState;
  words: ActionWords;
  moves: boolean;
  moved: boolean;
  retry: () => void;
  busy: boolean;
}) {
  if (state.kind === 'idle') return null;
  if (state.kind === 'sending') {
    return (
      <p role="status" className="bg-paper-2 text-ink-2 rounded-md px-3 py-2 text-[13.5px]">
        {words.doing}
      </p>
    );
  }
  if (state.kind === 'done') {
    const what = [
      state.to ? `Reenviado a ${state.to}` : null,
      state.folder ? `${moved ? 'movido' : 'copiado'} a «${state.folder}»` : null,
    ]
      .filter(Boolean)
      .join(' y ');
    // «Movido a …» on its own starts the sentence.
    const sentence = what.charAt(0).toUpperCase() + what.slice(1);
    return (
      <p role="status" className="bg-ok-soft text-ok rounded-md px-3 py-2 text-[13.5px]">
        {sentence} a las {formatDate(timeFormat, state.at)}.
      </p>
    );
  }
  const text = state.kind === 'failed' ? actionFailureText(state.reason, { moves }) : words.stalled;
  return (
    <div
      role="alert"
      className="bg-danger-soft text-danger flex flex-col gap-2 rounded-md px-3 py-2 text-[13.5px]"
    >
      <span>
        <span className="font-medium">{words.notDone}: </span>
        {text}
      </span>
      {state.kind === 'failed' ? <TechnicalDetails text={state.reason} /> : null}
      <div>
        <Button variant="secondary" size="sm" onClick={retry} disabled={busy}>
          Reintentar
        </Button>
      </div>
    </div>
  );
}
